import * as React from 'react';
import {usePrevious} from '../../hooks/usePrevious';
import {useAnimationFrame} from '../../hooks/useAnimationFrame';
import {DayItem} from '../DayItem';
import {isElementInView} from '../../utils/isElementInView';
import styles from './DayList.module.css';
import {AvailabilityApiResponseDay} from '../../../server/routes/availability/types';
import {useViewport, Viewport} from '@treatwell/ui';
import {Status} from '../../store/slices/availability/slice';
import {NavButtonNext, NavButtonPrevious} from './NavButton';

export type Day = Pick<
  AvailabilityApiResponseDay,
  'date' | 'dayNumber' | 'isAvailable' | 'dayName'
>;

export type NavigateProps = {
  onNavigateNext?: () => void;
  onNavigatePrevious?: () => void;
  isFirstWeek?: boolean;
  isLastWeek?: boolean;
  paginationFutureRequestStatus: Status;
};
type Props = {
  weeks: Day[][];
  onSelected?: (readableDate: string) => void;
  selectedDate: string | null;
  activeWeek?: number;
} & NavigateProps;

export const DayList = ({
  weeks,
  onSelected,
  selectedDate,
  activeWeek = 0,
  onNavigateNext,
  onNavigatePrevious,
  isLastWeek,
  isFirstWeek,
  paginationFutureRequestStatus,
}: Props) => {
  const previousActiveWeek = usePrevious(activeWeek);
  const containerRef = React.useRef<HTMLDivElement>(null);
  const weekRefs = React.useRef<HTMLDivElement[]>([]);
  const firstDayItemRef = React.useRef<HTMLButtonElement | null>(null);
  const secondDayItemRef = React.useRef<HTMLButtonElement | null>(null);
  const [weekGap, setWeekGap] = React.useState<number>(0);
  const isSmallViewport = useViewport({device: ['mobile', 'tablet']});
  const hideNavButtonNext =
    isLastWeek && !/pending|rejected/.test(paginationFutureRequestStatus);

  const onResize = useAnimationFrame(() => {
    const currentWeekGap = getWeekGap();
    setWeekGap(currentWeekGap);
    navigateToWeek({
      activeWeek,
      weekGap: currentWeekGap,
      scrollBehavior:
        previousActiveWeek !== undefined && previousActiveWeek !== activeWeek
          ? 'smooth'
          : 'auto',
    });
  }, [activeWeek, previousActiveWeek]);

  React.useLayoutEffect(() => {
    window.addEventListener('resize', onResize);

    onResize();

    return () => {
      window.removeEventListener('resize', onResize);
    };
  }, [onResize]);

  // Measures the distance between a DayItem so that same space
  // can be applied between the week elements to keep the spacing
  // consistent
  function getWeekGap(): number {
    if (!firstDayItemRef.current || !secondDayItemRef.current) {
      return 0;
    }

    const firstDayItemPosition =
      firstDayItemRef.current.getBoundingClientRect().right;
    const secondDayItemPosition =
      secondDayItemRef.current.getBoundingClientRect().left;

    return secondDayItemPosition - firstDayItemPosition;
  }

  function navigateToWeek({
    activeWeek,
    weekGap,
    scrollBehavior = 'auto',
  }: {
    activeWeek: number;
    weekGap: number;
    scrollBehavior?: ScrollBehavior;
  }) {
    // use the first week as a 'template' for the width measurement because
    // they're all the same width regardless
    const weekToMeasure = weekRefs.current[0];

    if (
      !containerRef.current ||
      !weekToMeasure ||
      isElementInView(weekRefs.current[activeWeek], containerRef.current)
    ) {
      return;
    }

    // work out how to scroll the container of the weeks:
    // 1. get the width of the first week to use as a template (we know all
    //    weeks are the same width)
    // 2. Multiply by the activeWeek so we know how many weeks we need to scroll
    // 3. Also add on the total gap between the weeks so we scroll accurately to
    //    the edge
    containerRef.current.scrollTo({
      left:
        weekToMeasure.getBoundingClientRect().width * activeWeek +
        weekGap * activeWeek,
      behavior: window.Cypress ? 'auto' : scrollBehavior,
    });
  }

  return (
    <div className={styles.root} data-cy="DayListContainer">
      <Viewport device="desktop">
        <NavButtonPrevious
          hide={isFirstWeek}
          onClick={(event) => {
            onNavigatePrevious?.();
            event.preventDefault();
          }}
        />
      </Viewport>
      <div
        ref={containerRef}
        className={styles.container}
        data-cy="DayList"
        onKeyDown={(e) => {
          /**
           * Prevents pagination with the arrow keys when focused on the DayItem.
           * This behaviour is caused by the scroll-snap-type and scroll-snap-align CSS properties.
           */
          if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
            e.preventDefault();
          }
        }}
      >
        {weeks.map((week, weekIndex) => (
          <div
            key={weekIndex}
            className={styles.week}
            style={weekIndex !== 0 ? {marginLeft: weekGap} : undefined}
            ref={(node) => {
              if (!node || weekRefs.current.includes(node)) {
                return;
              }
              weekRefs.current.push(node);
            }}
          >
            {week.map((day, dayIndex) => {
              return (
                <DayItem
                  key={day.date}
                  isSelected={selectedDate === day.date}
                  onSelected={onSelected}
                  isSmallViewport={isSmallViewport}
                  {...day}
                  dayRef={(node: HTMLButtonElement) => {
                    if (weekIndex === 0 && dayIndex === 0) {
                      firstDayItemRef.current = node;
                    } else if (weekIndex == 0 && dayIndex === 1) {
                      secondDayItemRef.current = node;
                    }
                  }}
                />
              );
            })}
          </div>
        ))}
      </div>
      <Viewport device="desktop">
        <NavButtonNext
          hide={hideNavButtonNext}
          isLoading={paginationFutureRequestStatus === 'pending'}
          onClick={(event) => {
            if (paginationFutureRequestStatus !== 'pending') {
              onNavigateNext?.();
            }
            event.preventDefault();
          }}
        />
      </Viewport>
    </div>
  );
};

export default DayList;
